/**
 * Calculates the total gross price for a player including all fees.
 * Formula: (BasePrice / NumPlayers) + (BasePrice / NumPlayers * (CommissionPct / 100)) + MinFee
 * 
 * @param basePrice - The total base price of the match or tournament
 * @param numPlayers - Number of players to split the base price (default: 4)
 * @param commissionPct - Platform commission percentage (e.g., 10 for 10%)
 * @param minFee - Fixed minimum service fee (Floor)
 * @returns The final amount to be paid by the player
 */
export const calculateGrossPricePerPlayer = (
    basePrice: number,
    numPlayers: number = 4,
    commissionPct: number = 0,
    minFee: number = 0
): number => {
    if (!basePrice || basePrice <= 0) return 0;
    const players = numPlayers || 4;
    const basePerPlayer = basePrice / players;
    const adminFeePerPlayer = basePerPlayer * (commissionPct / 100);

    // We round to 2 decimals at each step to match Stripe's behavior if needed, 
    // but typically we do it at the end.
    return basePerPlayer + adminFeePerPlayer + minFee;
};

/**
 * Returns the commission amount for a single player.
 */
export const calculateCommissionPerPlayer = (
    basePrice: number,
    numPlayers: number = 4,
    commissionPct: number = 0,
    minFee: number = 0
): number => {
    if (!basePrice || basePrice <= 0) return 0;
    const players = numPlayers || 4;
    const basePerPlayer = basePrice / players;
    return (basePerPlayer * (commissionPct / 100)) + minFee;
};

/**
 * Supported currencies for the platform.
 */
export const SUPPORTED_CURRENCIES = [
    { code: 'EUR', label: '🇪🇺 EUR (€) - Europe' },
    { code: 'USD', label: '🇺🇸 USD ($) - États-Unis' },
    { code: 'GBP', label: '🇬🇧 GBP (£) - Royaume-Uni' },
    { code: 'CHF', label: '🇨🇭 CHF (Fr) - Suisse' },
    { code: 'CAD', label: '🇨🇦 CAD ($) - Canada' },
    { code: 'AUD', label: '🇦🇺 AUD ($) - Australie' },
    { code: 'JPY', label: '🇯🇵 JPY (¥) - Japon' },
    { code: 'CNY', label: '🇨🇳 CNY (¥) - Chine' },
    { code: 'SEK', label: '🇸🇪 SEK (kr) - Suède' },
    { code: 'NOK', label: '🇳🇴 NOK (kr) - Norvège' },
    { code: 'DKK', label: '🇩🇰 DKK (kr) - Danemark' },
    { code: 'PLN', label: '🇵🇱 PLN (zł) - Pologne' },
    { code: 'BRL', label: '🇧🇷 BRL (R$) - Brésil' },
    { code: 'ARS', label: '🇦🇷 ARS ($) - Argentine' },
    { code: 'CLP', label: '🇨🇱 CLP ($) - Chili' },
    { code: 'COP', label: '🇨🇴 COP ($) - Colombie' },
    { code: 'PEN', label: '🇵🇪 PEN (S/) - Pérou' },
    { code: 'UYU', label: '🇺🇾 UYU ($) - Uruguay' },
    { code: 'PYG', label: '🇵🇾 PYG (₲) - Paraguay' },
    { code: 'MXN', label: '🇲🇽 MXN ($) - Mexique' },
    { code: 'INR', label: '🇮🇳 INR (₹) - Inde' },
    { code: 'NZD', label: '🇳🇿 NZD ($) - Nouvelle-Zélande' },
    { code: 'ZAR', label: '🇿🇦 ZAR (R) - Afrique du Sud' },
    { code: 'TRY', label: '🇹🇷 TRY (₺) - Turquie' },
    { code: 'AED', label: '🇦🇪 AED (د.إ) - Émirats Arabes Unis' },
    { code: 'SAR', label: '🇸🇦 SAR (﷼) - Arabie Saoudite' },
    { code: 'MAD', label: '🇲🇦 MAD (DH) - Maroc' },
    { code: 'QAR', label: '🇶🇦 QAR (QR) - Qatar' },
    { code: 'EGP', label: '🇪🇬 EGP (E£) - Égypte' },
    { code: 'CZK', label: '🇨🇿 CZK (Kč) - Tchéquie' },
    { code: 'RON', label: '🇷🇴 RON (L) - Roumanie' },
    { code: 'ILS', label: '🇮🇱 ILS (₪) - Israël' },
];

/**
 * Currencies that do not have subunits (cents) in Stripe.
 */
export const ZERO_DECIMAL_CURRENCIES = [
    'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'
];

/**
 * Checks if a currency is zero-decimal (no cents).
 */
export const isZeroDecimal = (currency: string): boolean => {
    if (!currency) return false;
    return ZERO_DECIMAL_CURRENCIES.includes(currency.toLowerCase());
};

/**
 * Returns the symbol for a given currency code.
 * @param currencyCode - ISO 4217 currency code (e.g., 'EUR')
 * @returns The symbol (e.g., '€') or the code if no symbol is found.
 */
export const getCurrencySymbol = (currencyCode?: string): string => {
    if (!currencyCode) return '';
    const code = currencyCode.toUpperCase();
    const found = SUPPORTED_CURRENCIES.find(c => c.code === code);
    if (found) {
        // Extract symbol from label "EUR (€) - Euro"
        const match = found.label.match(/\(([^)]+)\)/);
        return match ? match[1] : code;
    }
    return code;
};

/**
 * Formats a price using Intl.NumberFormat for consistent localized display.
 * @param amountInCents - The amount to format (in internal cents, where 1.00 EUR = 100 or 1 CLP = 100)
 * @param currency - ISO 4217 currency code (default: 'EUR')
 * @returns Formatted price string (e.g., "10,00 €" or "$10")
 */
export const formatPrice = (amountInCents: number, currency?: string): string => {
    if (!currency) return (amountInCents / 100).toFixed(2);
    try {
        // Our system always stores prices in "internal cents" (1 unit = 100)
        // Intl.NumberFormat expects the major unit (e.g., 10.00 for 10 EUR, 500 for 500 CLP)
        const amount = amountInCents / 100;

        return new Intl.NumberFormat(navigator.language, {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount);
    } catch (e) {
        // Fallback for invalid currency or navigator issues
        return (amountInCents / 100).toFixed(2) + ' ' + (currency?.toUpperCase() || 'EUR');
    }
};
/**
 * Converts an amount from EUR to the target currency.
 * 
 * @param amount - Amount in EUR (natural units, e.g. 0.40)
 * @param targetCurrency - Target ISO currency code (e.g. 'BRL')
 * @param rates - Object mapping currency codes to EUR rates (e.g. { 'BRL': 6.12 })
 * @returns Converted amount in target currency
 */
export const convertFromEUR = (amount: number, targetCurrency: string, rates: Record<string, any>): number => {
    const currency = targetCurrency.toUpperCase();
    if (currency === 'EUR') return amount;

    const rate = rates[currency];
    if (!rate) return amount;

    const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(numericRate) || numericRate <= 0) return amount;

    return amount * numericRate;
};
